/**
 * The Suggestion Engine's last few runs of this session, kept by their inputs (owner, 15 Oct 2026).
 *
 * Every run evaluates hundreds of scenarios server-side, which takes seconds. Re-opening a saved plan
 * mounts the panel afresh and would run them all again for the same answer; with the inputs
 * unchanged the answer is the same, so it is handed back at once. Anything that changes an input —
 * the profile, the room price, the living expenses, the settings, the horizon — changes the key and
 * runs again. The cache is in memory only: a page reload starts empty, and nothing is ever written to
 * the saved plan (see the 413 on save, 9 Oct 2026).
 */
const MAX_ENTRIES = 3;
const entries = new Map<string, unknown>();

/** The key is the run's inputs, verbatim; the progress callback is not an input. */
export function analysisCacheKey(inputs: unknown[]): string {
    return JSON.stringify(inputs);
}

export function cachedAnalysis<T>(key: string): T | undefined {
    const hit = entries.get(key);
    if (hit === undefined) return undefined;
    // Most recently used goes to the back, so the oldest is what falls off.
    entries.delete(key);
    entries.set(key, hit);
    return hit as T;
}

export function rememberAnalysis<T>(key: string, value: T): T {
    entries.delete(key);
    entries.set(key, value);
    while (entries.size > MAX_ENTRIES) {
        const oldest = entries.keys().next().value;
        if (oldest === undefined) break;
        entries.delete(oldest);
    }
    return value;
}

export function cachedAnalysisCount(): number {
    return entries.size;
}

export function forgetAnalyses(): void {
    entries.clear();
}
